<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { ref, computed } from "vue";
import {
  useToast,
  AtomText,
  AtomMoney,
  AtomButton,
  AtomInputMoney,
  SatFormControl,
} from "@chatfood/design-system";
import { I18nTranslate } from "../i18n-translate";
import { useBasketStore } from "~/stores/basket";
import { useCustomerStore } from "~/stores/customer";
import { useLocale } from "~/composables/use-locale";

const emits = defineEmits<{ close: [] }>();

const css = bemBuilder("loyalty-redeem-cash-form");

const { t } = useLocale();
const { toastError } = useToast();

const customer = useCustomerStore();
const basket = useBasketStore();

const isApplying = ref(false);

const cashWallet = computed(() => customer.loyalty.wallet.cash.balance);
const loyaltyWalletCashApplied = computed(() => basket.loyaltyWallet?.amount);

const creditsToBeUsed = computed(
  () => loyaltyWalletCashApplied.value ?? cashWallet.value,
);

const customCredits = ref(
  loyaltyWalletCashApplied.value ?? creditsToBeUsed.value,
);

const invalidMessage = computed(() => {
  if (customCredits.value.value > cashWallet.value.value) {
    return t("components.loyalty_redeem_cash_form.invalid_exceeds_wallet");
  }

  return "";
});

const onApplyCredits = async () => {
  if (invalidMessage.value) return;

  isApplying.value = true;

  try {
    await basket.applyLoyaltyWalletCash(customCredits.value);
    emits("close");
  } catch {
    toastError(t("components.loyalty_redeem_cash_form.error_toast"));
  } finally {
    isApplying.value = false;
  }
};
</script>

<template>
  <form :class="css()" @submit.prevent="onApplyCredits">
    <AtomText type="h3">
      {{ t("components.loyalty_redeem_cash_form.title") }}
    </AtomText>
    <I18nTranslate keypath="components.loyalty_redeem_cash_form.description">
      <template #amount>
        <AtomText type="body-bold">
          <AtomMoney
            :amount="cashWallet.value"
            :currency="cashWallet.currency"
          />
        </AtomText>
      </template>
    </I18nTranslate>
    <SatFormControl :error-message="invalidMessage">
      <AtomInputMoney
        :value="customCredits"
        :disabled="false"
        :has-error="Boolean(invalidMessage)"
        @input="(value) => (customCredits = value)"
      />
    </SatFormControl>
    <AtomButton
      size="large"
      :is-loading="isApplying"
      :is-disabled="customCredits.value <= 0 || Boolean(invalidMessage)"
      @click="onApplyCredits"
    >
      {{ t("components.loyalty_redeem_cash_form.cta_label") }}
    </AtomButton>
  </form>
</template>

<style lang="scss" scoped>
.loyalty-redeem-cash-form {
  display: flex;
  gap: var(--spacing-lg);
  flex-direction: column;
}
</style>
